import { motion } from 'framer-motion';
import { useState } from 'react';
import { Send, Mail, MapPin, Loader2, CheckCircle, Instagram, Youtube } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// X (Twitter) Icon component
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const socialLinks = [
  {
    icon: Instagram,
    href: 'https://instagram.com/soundmooofficial',
    label: 'Instagram',
    color: 'hover:text-[#E1306C]',
  },
  {
    icon: XIcon,
    href: 'https://x.com/soundmoo_',
    label: 'X',
    color: 'hover:text-white',
    isCustom: true,
  },
  {
    icon: Youtube,
    href: 'https://www.youtube.com/@soundmoo/featured',
    label: 'YouTube',
    color: 'hover:text-[#FF0000]',
  },
];

const interestOptions = [
  { value: 'licensing', label: 'Licensing' },
  { value: 'commission', label: 'Commission' },
  { value: 'distribution', label: 'Distribution' },
  { value: 'retail', label: 'Retail' },
  { value: 'other', label: 'Other' },
];

const regionOptions = [
  { value: 'north-america', label: 'North America' },
  { value: 'europe', label: 'Europe' },
  { value: 'asia-pacific', label: 'Asia Pacific' },
  { value: 'latin-america', label: 'Latin America' },
  { value: 'middle-east-africa', label: 'Middle East & Africa' },
  { value: 'global', label: 'Global' },
];

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    role: '',
    email: '',
    region: '',
    interest: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        company: '',
        role: '',
        email: '',
        region: '',
        interest: '',
        message: '',
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again shortly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section-padding relative overflow-hidden pb-32"
    >
      <div className="soundmoo-container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.span className="text-sm font-medium tracking-widest uppercase text-white/60 mb-4 block">
            Contact
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] mb-5">
            Start the Conversation
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Ready to explore partnership opportunities? Fill out the form below 
            and our team will be in touch shortly.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-1"
          >
            <div className="space-y-4">
              <div className="glass-card-dark p-5">
                <Mail className="w-5 h-5 text-white mb-3" />
                <h4 className="text-base font-semibold text-white font-['Outfit'] mb-1">
                  Get In Touch
                </h4>
                <p className="text-white/70 text-sm">
                  Use the contact form to get in touch with our team. We'll make sure your enquiry reaches the right person.
                </p>
              </div>

              <div className="glass-card-dark p-5">
                <MapPin className="w-5 h-5 text-white mb-3" />
                <h4 className="text-base font-semibold text-white font-['Outfit'] mb-1">
                  Global Operations
                </h4>
                <p className="text-white/70 text-sm">
                  We work with partners worldwide. Contact us for regional availability.
                </p>
              </div>

              <div className="glass-card-dark p-5">
                <h4 className="text-base font-semibold text-white font-['Outfit'] mb-3">
                  Follow Soundmoo
                </h4>
                <div className="flex items-center gap-3">
                  {socialLinks.map((social) => (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/70 ${social.color} transition-colors`}
                      whileHover={{ scale: 1.15, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      aria-label={social.label}
                      data-testid={`contact-social-${social.label.toLowerCase()}`}
                    >
                      {social.isCustom ? (
                        <social.icon className="w-4 h-4" />
                      ) : (
                        <social.icon className="w-4 h-4" />
                      )}
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="h-full flex items-center justify-center p-10 rounded-2xl glass-card-dark"
              >
                <div className="text-center">
                  <CheckCircle className="w-14 h-14 text-[#FBBF24] mx-auto mb-5" />
                  <h3 className="text-2xl font-bold text-white font-['Outfit'] mb-3">
                    Thank You for Reaching Out!
                  </h3>
                  <p className="text-white/80 mb-5 text-sm">
                    We've received your inquiry and will be in touch shortly.
                  </p>
                  <Button
                    data-testid="contact-reset-button"
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="bg-transparent border-white/30 text-white hover:bg-white/10"
                  >
                    Send Another Inquiry
                  </Button>
                </div>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="p-6 md:p-8 rounded-2xl glass-card-dark"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1.5">
                      Name *
                    </label>
                    <Input
                      data-testid="contact-name-input"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/40 focus:ring-white/20 h-10"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1.5">
                      Company *
                    </label>
                    <Input
                      data-testid="contact-company-input"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      placeholder="Company name"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/40 focus:ring-white/20 h-10"
                    />
                  </div>

                  {/* Role */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1.5">
                      Role
                    </label>
                    <Input
                      data-testid="contact-role-input"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      placeholder="Your role"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/40 focus:ring-white/20 h-10"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1.5">
                      Email *
                    </label>
                    <Input
                      data-testid="contact-email-input"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="work@company.com"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/40 focus:ring-white/20 h-10"
                    />
                  </div>

                  {/* Region */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1.5">
                      Region *
                    </label>
                    <Select
                      value={formData.region}
                      onValueChange={(value) => handleSelectChange('region', value)}
                    >
                      <SelectTrigger
                        data-testid="contact-region-select"
                        className="bg-white/10 border-white/20 text-white focus:ring-white/20 h-10"
                      >
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0E2E6E] border-white/20 z-[100]">
                        {regionOptions.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Interest */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1.5">
                      Interest *
                    </label>
                    <Select
                      value={formData.interest}
                      onValueChange={(value) => handleSelectChange('interest', value)}
                    >
                      <SelectTrigger
                        data-testid="contact-interest-select"
                        className="bg-white/10 border-white/20 text-white focus:ring-white/20 h-10"
                      >
                        <SelectValue placeholder="Select interest" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0E2E6E] border-white/20 z-[100]">
                        {interestOptions.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Message */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-white/80 mb-1.5">
                    Message
                  </label>
                  <Textarea
                    data-testid="contact-message-input"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your interest in Soundmoo..."
                    rows={3}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-white/40 focus:ring-white/20 resize-none"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <p className="text-red-300 text-sm mb-4">{error}</p>
                )}

                {/* Submit Button */}
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    data-testid="contact-submit-button"
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-white text-[#0A1A3F] hover:bg-white/90 flex items-center justify-center gap-2 h-12 font-semibold rounded-full"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Start the Conversation
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </Button>
                </motion.div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
